import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, PopupData, Service } from 'webapp-types';
import { BillingFeature, Direction, ManualSyncPopupType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import joinClasses from 'webapp/helpers/join-classes';
import CommonPopupsDataStatusComponent from 'webapp/components/common/popups/data-status';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type Board from 'webapp/models-custom/board';

interface Args {
	model: Board;
	trackingOrigin?: string;
	skipActions?: boolean;
	popupTheme?: string;
	iconSize?: IconSize;
}

export default class BoardManualSyncButtonComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked isSyncing = false;
	@tracked isDisabled;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// disable based on billing limitations
		this.isDisabled = !this.billingService.instantRefreshEnabled;
	}

	get isShown() {
		return this.currentUserService.isLoggedIn && !this.currentUserService.isDemo;
	}

	get text() {
		return this.isSyncing ? 'Syncing...' : 'Refresh';
	}

	get showActions(): boolean {
		return !this.args.skipActions;
	}

	get doAllSourcesHavePrioritySync() {
		return this.args.model.sourcesObjs.every((source) => source.customSyncInterval === 15);
	}

	get iconSize(): IconSize {
		return this.args.iconSize ?? 'xs';
	}

	@action
	triggerSync(e: MouseEvent) {
		if (this.isSyncing) {
			return;
		}

		if (
			this.navigationService.isDatawallApp &&
			!this.navigationService.isDatawallAndHasAccessToBoard
		) {
			return;
		}

		this.analyticsService.event(TrackingEvent.BOARD_REFRESH_DATA, {
			origin: this.args.trackingOrigin,
		});

		const openLimitationPopup = () => {
			const modelId = this.args.model.boardIdForPublicUrl;
			if (this.navigationService.isDatawallApp && modelId) {
				if (this.doAllSourcesHavePrioritySync) {
					this.openPopup(e.target as HTMLElement, ManualSyncPopupType.LIMIT_REACHED);
				} else {
					this.openPopup(e.target as HTMLElement, ManualSyncPopupType.LIMITATION);
				}
			} else if (this.accountService.isAdminButNotInClientSpace) {
				if (this.billingService.checkIfCan(BillingFeature.INSTANT_REFRESH, true)) {
					this.mmSourcesService.openEnableCustomDataUpdatePopup(
						this.args.model.sourcesObjs,
						true,
						this.args.trackingOrigin,
					);
				}
			} else {
				this.mmSourcesService.openContactOwnerPopup();
			}
		};

		if (this.isDisabled) {
			openLimitationPopup();
			return;
		}

		this.isSyncing = true;
		const { model } = this.args,
			payload: APIData.Board.DataRefetch = {};

		if (model.previewHash) {
			payload.previewHash = model.previewHash;
		} else {
			payload.boardId = model.numericId || undefined;
		}

		return this.mmMetricsService
			.requestDataRefetch(payload)
			.then((data) => {
				if (!data.success) {
					openLimitationPopup();
					this.isDisabled = true;
				} else if (!this.doAllSourcesHavePrioritySync) {
					this.openPopup(e.target as HTMLElement, ManualSyncPopupType.UPSELL);
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.finally(() => {
				this.isSyncing = false;
			});
	}

	@action
	openPopup(el: HTMLElement, type: ManualSyncPopupType) {
		const popup = this.popupService.createContext(CommonPopupsDataStatusComponent, el, {
				popupClass: 'popup-type-data-status',
				offset: 8,
				directions: [Direction.UP, Direction.DOWN, Direction.RIGHT, Direction.LEFT],
				theme: this.args.popupTheme,
			}),
			popupProperties: PopupData.Common.DataStatus = {
				context: 'board',
				lastSync: this.args.model.lastSync,
				showActions: this.showActions,
				manualSyncUpsellType: type,
				openEnableCustomDataUpdatePopup: () => {
					this.mmBoardsService.openEnableCustomDataUpdatePopup(
						this.args.model,
						this.args.trackingOrigin,
					);
				},
			};

		return popup.open(popupProperties);
	}
	<template>
		{{#if this.isShown}}
			<a
				role="button"
				class={{joinClasses
					(array
						"manual-sync-button"
						(if this.isSyncing "manual-sync-button--syncing")
						(if this.isDisabled "manual-sync-button--disabled")
					)
				}}
				{{on "click" this.triggerSync}}
			>
				<CommonUiIconComponent @name="Refresh" @size={{this.iconSize}} />
				<span>{{this.text}}</span>
			</a>
		{{/if}}
	</template>
}
